import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lms/shared-types';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api, listResource } from '@/lib/api';
import { customFieldSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { usePermissions } from '@/hooks/usePermissions';
import { customFieldUsesFullWidth, type CustomFieldFormWidth } from '@/lib/custom-field-form-order';
import {
  USER_CUSTOM_FIELD_TYPES,
  fieldTypeHasFileUploads,
  fieldTypeHasOptions,
  slugifyFieldName,
} from '@/lib/user-custom-field-types';
import { UserCustomFieldToggle } from './UserCustomFieldToggle';

interface FieldOption {
  label: string;
  value: string;
  isActive: boolean;
}

interface CustomFieldForm {
  name: string;
  slug: string;
  groupId: string;
  type: string;
  placeholder: string;
  description: string;
  helpText: string;
  defaultValue: string;
  sortOrder: number;
  isActive: boolean;
  isRequired: boolean;
  isVisible: boolean;
  isReadonly: boolean;
  validationMinLength: string;
  validationMaxLength: string;
  validationRegex: string;
  validationMinValue: string;
  validationMaxValue: string;
  validationMaxFileSizeKb: string;
  validationAllowedFileTypes: string;
  allowMultipleFiles: boolean;
  maxFiles: number;
  optionsLayout: 'vertical' | 'horizontal';
  formWidth: CustomFieldFormWidth;
  visibilityAdmins: boolean;
  visibilityInstructors: boolean;
  visibilitySelf: boolean;
  visibilityEditableSelf: boolean;
  visibilityHidden: boolean;
}

interface CustomFieldDetail {
  id: string;
  name: string;
  slug: string;
  type: string;
  groupId?: string;
  options?: FieldOption[];
  placeholder?: string;
  description?: string;
  helpText?: string;
  defaultValue?: string;
  sortOrder?: number;
  isActive?: boolean;
  isRequired?: boolean;
  isVisible?: boolean;
  isReadonly?: boolean;
  validationMinLength?: string;
  validationMaxLength?: string;
  validationRegex?: string;
  validationMinValue?: string;
  validationMaxValue?: string;
  validationMaxFileSizeKb?: string;
  validationAllowedFileTypes?: string;
  allowMultipleFiles?: boolean;
  maxFiles?: number;
  optionsLayout?: 'vertical' | 'horizontal';
  formWidth?: CustomFieldFormWidth | null;
  visibilityAdmins?: boolean;
  visibilityInstructors?: boolean;
  visibilitySelf?: boolean;
  visibilityEditableSelf?: boolean;
  visibilityHidden?: boolean;
}

interface FieldGroup {
  id: string;
  name: string;
}

const SECTION = 'lms-credentials-glass-panel rounded-xl border border-dash-outline-variant p-6 shadow-sm md:p-8';
const LABEL = 'text-sm font-medium text-dash-on-surface-variant';
const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-3 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const DEFAULT_VALUES: CustomFieldForm = {
  name: '',
  slug: '',
  groupId: '',
  type: 'text',
  placeholder: '',
  description: '',
  helpText: '',
  defaultValue: '',
  sortOrder: 0,
  isActive: true,
  isRequired: false,
  isVisible: true,
  isReadonly: false,
  validationMinLength: '',
  validationMaxLength: '',
  validationRegex: '',
  validationMinValue: '',
  validationMaxValue: '',
  validationMaxFileSizeKb: '',
  validationAllowedFileTypes: '',
  allowMultipleFiles: false,
  maxFiles: 1,
  optionsLayout: 'vertical',
  formWidth: 'half',
  visibilityAdmins: true,
  visibilityInstructors: false,
  visibilitySelf: true,
  visibilityEditableSelf: true,
  visibilityHidden: false,
};

export function UserCustomFieldFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const isEdit = Boolean(id);
  const canDelete = isEdit && hasPermission(PERMISSIONS.CAMPOS_USUARIO_ELIMINAR);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [options, setOptions] = useState<FieldOption[]>([{ label: '', value: '', isActive: true }]);

  const { data: existing, isLoading } = useQuery({
    queryKey: ['user-custom-fields', id],
    queryFn: () => api<CustomFieldDetail>(`/user-custom-fields/${id}`),
    enabled: isEdit,
  });

  const { data: groupsData } = useQuery({
    queryKey: ['user-custom-field-groups'],
    queryFn: () => api<FieldGroup[]>('/user-custom-fields/groups'),
  });

  const groups = groupsData ?? [];

  useEffect(() => {
    if (!existing) return;
    const rows = (existing.options ?? []).filter((option) => option.label.trim() || option.value.trim());
    setOptions(
      rows.length > 0
        ? rows.map((option) => ({
            label: option.label,
            value: option.value,
            isActive: option.isActive !== false,
          }))
        : [{ label: '', value: '', isActive: true }],
    );
  }, [existing]);

  const { register, handleSubmit, control, setValue } = useForm<CustomFieldForm>({
    values: existing
      ? {
          ...DEFAULT_VALUES,
          name: existing.name,
          slug: existing.slug,
          type: existing.type,
          groupId: existing.groupId ?? '',
          placeholder: existing.placeholder ?? '',
          description: existing.description ?? '',
          helpText: existing.helpText ?? '',
          defaultValue: existing.defaultValue ?? '',
          sortOrder: existing.sortOrder ?? 0,
          isActive: existing.isActive ?? true,
          isRequired: existing.isRequired ?? false,
          isVisible: existing.isVisible ?? true,
          isReadonly: existing.isReadonly ?? false,
          validationMinLength: existing.validationMinLength ?? '',
          validationMaxLength: existing.validationMaxLength ?? '',
          validationRegex: existing.validationRegex ?? '',
          validationMinValue: existing.validationMinValue ?? '',
          validationMaxValue: existing.validationMaxValue ?? '',
          validationMaxFileSizeKb: existing.validationMaxFileSizeKb ?? '',
          validationAllowedFileTypes: existing.validationAllowedFileTypes ?? '',
          allowMultipleFiles: existing.allowMultipleFiles ?? false,
          maxFiles: existing.maxFiles ?? 1,
          optionsLayout: existing.optionsLayout === 'horizontal' ? 'horizontal' : 'vertical',
          formWidth:
            existing.formWidth === 'full' || existing.formWidth === 'half'
              ? existing.formWidth
              : customFieldUsesFullWidth(existing)
                ? 'full'
                : 'half',
          visibilityAdmins: existing.visibilityAdmins ?? true,
          visibilityInstructors: existing.visibilityInstructors ?? false,
          visibilitySelf: existing.visibilitySelf ?? true,
          visibilityEditableSelf: existing.visibilityEditableSelf ?? true,
          visibilityHidden: existing.visibilityHidden ?? false,
        }
      : undefined,
    defaultValues: DEFAULT_VALUES,
  });

  const fieldType = useWatch({ control, name: 'type' });
  const allowMultipleFiles = useWatch({ control, name: 'allowMultipleFiles' });
  const maxFiles = useWatch({ control, name: 'maxFiles' });
  const selectedGroupId = useWatch({ control, name: 'groupId' });
  const showOptions = fieldTypeHasOptions(fieldType);
  const showFileUploads = fieldTypeHasFileUploads(fieldType);

  useEffect(() => {
    if (isEdit || selectedGroupId) return;
    const general = groups.find((group) => group.name === 'General') ?? groups[0];
    if (general) setValue('groupId', general.id);
  }, [groups, isEdit, selectedGroupId, setValue]);

  useEffect(() => {
    if (showFileUploads && allowMultipleFiles && Number(maxFiles) < 2) {
      setValue('maxFiles', 2);
    }
  }, [allowMultipleFiles, maxFiles, setValue, showFileUploads]);

  const mutation = useMutation({
    mutationFn: (values: CustomFieldForm) => {
      const body = {
        name: values.name.trim(),
        slug: values.slug.trim() || slugifyFieldName(values.name),
        type: values.type,
        groupId: values.groupId || undefined,
        placeholder: values.placeholder,
        description: values.description,
        helpText: values.helpText,
        defaultValue: values.defaultValue,
        sortOrder: Number.isFinite(values.sortOrder) ? values.sortOrder : 0,
        isActive: values.isActive,
        isRequired: values.isRequired,
        isVisible: values.isVisible,
        isReadonly: values.isReadonly,
        validationMinLength: values.validationMinLength,
        validationMaxLength: values.validationMaxLength,
        validationRegex: values.validationRegex,
        validationMinValue: values.validationMinValue,
        validationMaxValue: values.validationMaxValue,
        validationMaxFileSizeKb: values.validationMaxFileSizeKb,
        validationAllowedFileTypes: values.validationAllowedFileTypes,
        allowMultipleFiles: fieldTypeHasFileUploads(values.type) ? values.allowMultipleFiles : false,
        maxFiles: fieldTypeHasFileUploads(values.type) ? values.maxFiles : 1,
        optionsLayout: values.type === 'multiselect' && values.optionsLayout === 'horizontal' ? 'horizontal' : 'vertical',
        formWidth: values.formWidth === 'full' ? 'full' : 'half',
        visibilityAdmins: values.visibilityAdmins,
        visibilityInstructors: values.visibilityInstructors,
        visibilitySelf: values.visibilitySelf,
        visibilityEditableSelf: values.visibilityEditableSelf,
        visibilityHidden: values.visibilityHidden,
        options: fieldTypeHasOptions(values.type) ? options : undefined,
      };
      return isEdit
        ? api(`/user-custom-fields/${id}`, { method: 'PATCH', body })
        : api('/user-custom-fields', { method: 'POST', body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['user-custom-fields'] });
      void queryClient.invalidateQueries({ queryKey: ['user-custom-field-groups'] });
      if (isEdit && id) void queryClient.invalidateQueries({ queryKey: ['user-custom-fields', id] });
      toast(customFieldSuccessToast(isEdit ? 'edit' : 'create'));
      navigate('/campos-personalizados');
    },
    onError: (error) => {
      toast(
        roleActionErrorToast(
          error,
          isEdit ? 'No se pudo guardar el campo personalizado' : 'No se pudo crear el campo personalizado',
        ),
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api(`/user-custom-fields/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['user-custom-fields'] });
      void queryClient.invalidateQueries({ queryKey: ['user-custom-field-groups'] });
      toast(customFieldSuccessToast('delete'));
      navigate('/campos-personalizados');
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el campo personalizado'));
    },
  });

  function addOption() {
    setOptions((prev) => [...prev, { label: '', value: '', isActive: true }]);
  }

  function updateOption(index: number, patch: Partial<FieldOption>) {
    setOptions((prev) => prev.map((opt, i) => (i === index ? { ...opt, ...patch } : opt)));
  }

  function removeOption(index: number) {
    setOptions((prev) => prev.filter((_, i) => i !== index));
  }

  const footer = (
    <div className="flex flex-col-reverse items-stretch justify-end gap-4 border-t border-dash-outline-variant pt-8 sm:flex-row sm:items-center">
      {canDelete ? (
        <button
          type="button"
          className="mr-auto inline-flex items-center justify-center rounded-full border border-error-200 px-8 py-3 text-sm font-bold text-error-600 transition-all hover:bg-error-50 active:scale-[0.98] sm:w-auto"
          onClick={() => setDeleteOpen(true)}
        >
          Eliminar
        </button>
      ) : null}
      <Link
        to="/campos-personalizados"
        className="inline-flex items-center justify-center rounded-full border border-dash-primary px-8 py-3 text-sm font-bold text-dash-primary transition-all hover:bg-brand-50 active:scale-[0.98] sm:w-auto"
      >
        Cancelar
      </Link>
      <button
        type="submit"
        form="user-custom-field-form"
        disabled={mutation.isPending}
        className="inline-flex items-center justify-center rounded-full bg-dash-primary px-10 py-3 text-sm font-bold text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] sm:w-auto disabled:opacity-60"
      >
        {mutation.isPending ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear campo'}
      </button>
    </div>
  );

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink to="/campos-personalizados" label="Volver a campos del perfil de usuario" />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
            {isEdit ? 'Editar campo personalizado' : 'Nuevo campo personalizado'}
          </h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            {isEdit
              ? 'Actualice la configuración de este campo en los perfiles de usuario.'
              : 'Configure un nuevo campo adicional para los perfiles de usuario.'}
          </p>
        </div>
      </div>

      {isEdit && isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <form
          id="user-custom-field-form"
          className="space-y-6"
          onSubmit={handleSubmit((v) => mutation.mutate(v))}
        >
          <section className={SECTION}>
            <div className="mb-6 flex items-center gap-2 text-xl font-semibold text-dash-primary md:text-2xl">
              <span className="material-symbols-outlined text-[1.75rem] md:text-[1.85rem]" aria-hidden="true">
                info
              </span>
              Información general
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="name" className={LABEL}>
                  Nombre <span className="text-error-600">*</span>
                </label>
                <input id="name" type="text" required className={FIELD} {...register('name', { required: true })} />
              </div>
              <div className="space-y-2">
                <label htmlFor="slug" className={LABEL}>
                  Slug
                </label>
                <input id="slug" type="text" className={FIELD} {...register('slug')} />
                <p className="text-xs text-dash-on-surface-variant">Se genera automáticamente si se deja vacío.</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <label htmlFor="groupId" className={LABEL}>
                    Grupo
                  </label>
                  <Link
                    to="/campos-personalizados/grupos"
                    className="text-xs font-medium text-dash-primary hover:underline"
                  >
                    Gestionar grupos
                  </Link>
                </div>
                <div className="relative">
                  <select id="groupId" className={`${FIELD} cursor-pointer appearance-none pr-10`} {...register('groupId')}>
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-dash-on-surface-variant" aria-hidden="true">
                    <span className="material-symbols-outlined">expand_more</span>
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="type" className={LABEL}>
                  Tipo <span className="text-error-600">*</span>
                </label>
                <div className="relative">
                  <select id="type" required className={`${FIELD} cursor-pointer appearance-none pr-10`} {...register('type', { required: true })}>
                    {USER_CUSTOM_FIELD_TYPES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-dash-on-surface-variant" aria-hidden="true">
                    <span className="material-symbols-outlined">expand_more</span>
                  </span>
                </div>
              </div>
              <div className="space-y-2 md:col-span-2">
                <label htmlFor="placeholder" className={LABEL}>
                  Placeholder
                </label>
                <input id="placeholder" type="text" className={FIELD} {...register('placeholder')} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label htmlFor="description" className={LABEL}>
                  Descripción
                </label>
                <textarea id="description" rows={3} className={`${FIELD} resize-none`} {...register('description')} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label htmlFor="helpText" className={LABEL}>
                  Texto de ayuda
                </label>
                <input id="helpText" type="text" className={FIELD} {...register('helpText')} />
              </div>
              <div className="space-y-2">
                <label htmlFor="defaultValue" className={LABEL}>
                  Valor por defecto
                </label>
                <input id="defaultValue" type="text" className={FIELD} {...register('defaultValue')} />
              </div>
              <div className="space-y-2">
                <label htmlFor="sortOrder" className={LABEL}>
                  Orden
                </label>
                <input id="sortOrder" type="number" min={0} className={FIELD} {...register('sortOrder', { valueAsNumber: true })} />
              </div>
              <div className="space-y-2">
                <label htmlFor="formWidth" className={LABEL}>
                  Ancho
                </label>
                <div className="relative">
                  <select id="formWidth" className={`${FIELD} cursor-pointer appearance-none pr-10`} {...register('formWidth')}>
                    <option value="half">Mitad</option>
                    <option value="full">Largo</option>
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-dash-on-surface-variant" aria-hidden="true">
                    <span className="material-symbols-outlined">expand_more</span>
                  </span>
                </div>
                <p className="text-xs text-dash-on-surface-variant">Largo ocupa toda la fila. Mitad comparte la fila con otro campo.</p>
              </div>
            </div>
          </section>

          <section className={SECTION}>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4">
              <UserCustomFieldToggle name="isActive" label="Activo" register={register} />
              <UserCustomFieldToggle name="isRequired" label="Obligatorio" register={register} />
              <UserCustomFieldToggle name="isVisible" label="Visible" register={register} />
              <UserCustomFieldToggle name="isReadonly" label="Solo lectura" register={register} />
            </div>
          </section>

          {showFileUploads ? (
            <section className={SECTION}>
              <div className="mb-6 flex items-center gap-2 text-xl font-semibold text-dash-primary md:text-2xl">
                <span className="material-symbols-outlined text-[1.75rem] md:text-[1.85rem]" aria-hidden="true">
                  {fieldType === 'image' ? 'add_photo_alternate' : 'attach_file'}
                </span>
                {fieldType === 'image' ? 'Imágenes' : 'Archivos'}
              </div>
              <div className="grid gap-8 md:grid-cols-2">
                <div className="space-y-4 md:border-r md:border-dash-outline-variant md:pr-6">
                  <p className="text-xs font-bold uppercase tracking-wider text-dash-on-surface-variant">Subida</p>
                  <UserCustomFieldToggle
                    name="allowMultipleFiles"
                    label="Permitir múltiples archivos"
                    register={register}
                    className="group"
                  />
                  <p className="text-sm text-dash-on-surface-variant">
                    {allowMultipleFiles
                      ? `Los usuarios podrán adjuntar más de un ${fieldType === 'image' ? 'archivo de imagen' : 'archivo'}.`
                      : `Por defecto se permite un solo ${fieldType === 'image' ? 'archivo de imagen' : 'archivo'}.`}
                  </p>
                </div>
                <div className="space-y-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-dash-on-surface-variant">Límite</p>
                  {allowMultipleFiles ? (
                    <div className="max-w-xs space-y-2">
                      <label htmlFor="maxFiles" className={LABEL}>
                        Máximo de archivos
                      </label>
                      <input
                        id="maxFiles"
                        type="number"
                        min={2}
                        max={20}
                        className={FIELD}
                        {...register('maxFiles', { valueAsNumber: true, min: 2, max: 20 })}
                      />
                      <p className="text-xs text-dash-on-surface-variant">Entre 2 y 20.</p>
                    </div>
                  ) : (
                    <p className="text-sm text-dash-on-surface-variant">Se permitirá un solo archivo.</p>
                  )}
                </div>
              </div>
            </section>
          ) : null}

          <section className={SECTION}>
            <div className="mb-6 flex items-center gap-2 text-xl font-semibold text-dash-primary md:text-2xl">
              <span className="material-symbols-outlined text-[1.75rem] md:text-[1.85rem]" aria-hidden="true">
                verified_user
              </span>
              Validaciones
            </div>
            <div className="grid gap-8 md:grid-cols-2">
              <div className="space-y-4 md:border-r md:border-dash-outline-variant md:pr-6">
                <p className="text-xs font-bold uppercase tracking-wider text-dash-on-surface-variant">Texto y formato</p>
                <div className="flex gap-4">
                  <div className="flex-1 space-y-2">
                    <label htmlFor="validationMinLength" className={LABEL}>
                      Long. mínima
                    </label>
                    <input id="validationMinLength" type="number" min={0} className={FIELD} {...register('validationMinLength')} />
                  </div>
                  <div className="flex-1 space-y-2">
                    <label htmlFor="validationMaxLength" className={LABEL}>
                      Long. máxima
                    </label>
                    <input id="validationMaxLength" type="number" min={1} className={FIELD} {...register('validationMaxLength')} />
                  </div>
                </div>
                <div className="space-y-2">
                  <label htmlFor="validationRegex" className={LABEL}>
                    Expresión regular (Regex)
                  </label>
                  <input id="validationRegex" type="text" placeholder="/^[a-z]+$/i" className={FIELD} {...register('validationRegex')} />
                </div>
              </div>
              <div className="space-y-4">
                <p className="text-xs font-bold uppercase tracking-wider text-dash-on-surface-variant">Valores y archivos</p>
                <div className="flex gap-4">
                  <div className="flex-1 space-y-2">
                    <label htmlFor="validationMinValue" className={LABEL}>
                      Valor mínimo
                    </label>
                    <input id="validationMinValue" type="number" step="any" className={FIELD} {...register('validationMinValue')} />
                  </div>
                  <div className="flex-1 space-y-2">
                    <label htmlFor="validationMaxValue" className={LABEL}>
                      Valor máximo
                    </label>
                    <input id="validationMaxValue" type="number" step="any" className={FIELD} {...register('validationMaxValue')} />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-1 space-y-2">
                    <label htmlFor="validationMaxFileSizeKb" className={LABEL}>
                      Tam. máx. (KB)
                    </label>
                    <input id="validationMaxFileSizeKb" type="number" min={1} placeholder="2048" className={FIELD} {...register('validationMaxFileSizeKb')} />
                  </div>
                  <div className="flex-1 space-y-2">
                    <label htmlFor="validationAllowedFileTypes" className={LABEL}>
                      Tipos de archivo
                    </label>
                    <input id="validationAllowedFileTypes" type="text" placeholder="pdf,jpg,png" className={FIELD} {...register('validationAllowedFileTypes')} />
                    <p className="text-xs text-dash-on-surface-variant">Ej: pdf,jpg,png</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className={SECTION}>
            <div className="mb-6 flex items-center gap-2 text-xl font-semibold text-dash-primary md:text-2xl">
              <span className="material-symbols-outlined text-[1.75rem] md:text-[1.85rem]" aria-hidden="true">
                visibility
              </span>
              Visibilidad y permisos
            </div>
            <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
              <UserCustomFieldToggle name="visibilityAdmins" label="Visible para administradores" register={register} className="group" />
              <UserCustomFieldToggle name="visibilityInstructors" label="Visible para instructores" register={register} className="group" />
              <UserCustomFieldToggle name="visibilitySelf" label="Visible para el propio usuario" register={register} className="group" />
              <UserCustomFieldToggle name="visibilityEditableSelf" label="Editable por el usuario" register={register} className="group" />
              <UserCustomFieldToggle name="visibilityHidden" label="Oculto" register={register} className="group" />
            </div>
          </section>

          {showOptions && (
            <section className={SECTION}>
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-dash-primary md:text-2xl">Opciones</h3>
                  <p className="text-sm text-dash-on-surface-variant">Para Select, Radio y Multi Select.</p>
                </div>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-lg border border-dash-outline-variant px-4 py-2 text-sm font-medium text-dash-primary transition-colors hover:bg-secondary-100"
                  onClick={addOption}
                >
                  Agregar opción
                </button>
              </div>
              {fieldType === 'multiselect' ? (
                <div className="mb-6 space-y-2">
                  <label htmlFor="optionsLayout" className={LABEL}>
                    Disposición
                  </label>
                  <div className="relative">
                    <select id="optionsLayout" className={`${FIELD} cursor-pointer appearance-none pr-10`} {...register('optionsLayout')}>
                      <option value="vertical">Hacia abajo</option>
                      <option value="horizontal">Hacia el lado</option>
                    </select>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-dash-on-surface-variant" aria-hidden="true">
                      <span className="material-symbols-outlined">expand_more</span>
                    </span>
                  </div>
                </div>
              ) : null}
              <div className="space-y-3">
                {options.map((option, index) => (
                  <div
                    key={index}
                    className="grid gap-3 rounded-lg border border-dash-outline-variant bg-white p-4 lg:grid-cols-[1fr_1fr_auto_auto]"
                  >
                    <input
                      type="text"
                      value={option.label}
                      onChange={(e) => updateOption(index, { label: e.target.value })}
                      placeholder="Etiqueta"
                      className="w-full rounded-lg border border-dash-outline-variant px-3 py-2 text-sm outline-none focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20"
                    />
                    <input
                      type="text"
                      value={option.value}
                      onChange={(e) => updateOption(index, { value: e.target.value })}
                      placeholder="Valor"
                      className="w-full rounded-lg border border-dash-outline-variant px-3 py-2 text-sm outline-none focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20"
                    />
                    <label className="flex items-center gap-3 text-sm text-dash-on-surface">
                      <span className="relative inline-block h-6 w-11 shrink-0">
                        <input
                          type="checkbox"
                          className="peer sr-only"
                          checked={option.isActive}
                          onChange={(e) => updateOption(index, { isActive: e.target.checked })}
                        />
                        <span className="block h-6 w-11 rounded-full bg-secondary-200 transition-colors peer-checked:bg-dash-primary" aria-hidden="true" />
                        <span className="pointer-events-none absolute left-0.5 top-0.5 block h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" aria-hidden="true" />
                      </span>
                      Activa
                    </label>
                    <button type="button" className="text-sm font-medium text-error-600 hover:underline" onClick={() => removeOption(index)}>
                      Eliminar
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {footer}
        </form>
      )}

      <ConfirmModal
        open={deleteOpen}
        title="Eliminar campo personalizado"
        description={`¿Está seguro de eliminar «${existing?.name ?? 'este campo'}»? Se borrarán también los valores guardados en los usuarios.`}
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setDeleteOpen(false)}
      />
    </div>
  );
}
